import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CenteredCardTempScreen from './ui/CenteredCardTempScreen';
import Headline from './ui/Headline';
import LinkButton from './ui/LinkButton';
import { DownloadStartedMark } from './ui/DownloadStartedMark';
import { DatabaseSettingsModel } from '../services/DatabaseSettings';

type Platform = 'mac' | 'windows';

function readPlatform(searchParams: URLSearchParams): Platform {
    return searchParams.get('platform') === 'windows' ? 'windows' : 'mac';
}

// Fires the actual installer download. These URLs serve as attachments via
// Content-Disposition (see DatabaseSettingsModel.binaryDownloadUrl), so an
// off-DOM anchor click triggers the browser's native download without
// navigating away from this confirmation screen.
function triggerDownload(url: string) {
    const link = document.createElement('a');
    link.href = url;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
}

// Where the Account page's "Download macOS" / "Download Windows" buttons send
// the user, instead of firing the download silently off an <a href> — confirms
// it started and points them at the next step. Re-derives the download URL
// itself (platform via ?platform=, version via a fresh fetch) rather than
// trusting navigation state, so a refresh or a bookmarked link still works.
function DownloadStartedPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const platform = readPlatform(searchParams);

    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

    useEffect(() => {
        DatabaseSettingsModel.getCurrentVersion()
            .then((version) => {
                if (!version) return;
                setDownloadUrl(
                    platform === 'mac'
                        ? DatabaseSettingsModel.getMacDownloadUrl(version)
                        : DatabaseSettingsModel.getWindowsDownloadUrl(version),
                );
            })
            .catch((error) => console.error('Error loading plugin version:', error));
    }, [platform]);

    useEffect(() => {
        if (downloadUrl) triggerDownload(downloadUrl);
    }, [downloadUrl]);

    return (
        <CenteredCardTempScreen size="notice" className="relative">
            <button
                onClick={() => navigate('/account')}
                aria-label="Close"
                className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full border border-hairline text-ink-secondary transition-opacity hover:opacity-60"
            >
                <svg viewBox="0 0 10 10" className="h-2.5 w-2.5" fill="none" aria-hidden>
                    <path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
            </button>

            <DownloadStartedMark />

            <Headline stacked lead="Download" trail="Started." className="text-center" />

            <p className="max-w-[300px] text-sub font-bold text-copy">
                Open the file to install, then load the Organic Electronics Instrument in your DAW.
            </p>

            <div className="mt-auto flex flex-wrap items-baseline justify-center gap-1.5 pt-6">
                <span className="text-ui font-semibold text-card-muted-soft">Didn&rsquo;t get the file?</span>
                <LinkButton tone="secondary" onClick={() => downloadUrl && triggerDownload(downloadUrl)}>
                    Download again
                </LinkButton>
            </div>
        </CenteredCardTempScreen>
    );
}

export default DownloadStartedPage;
